import { Box, Button, Link, Paper, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'

const ALERT_VARIANTS = ['warning', 'success', 'info', 'error']
const MAX_ACTIONS = 2
// only http(s) links are linkified/rendered — blocks javascript: and other schemes
const HTTP_URL_PATTERN = /^https?:\/\//i
const MARKDOWN_LINK_PATTERN = /\[([^\]]+)\]\((\S+)\)/g

function renderBodyWithLinks(body) {
  const nodes = []
  let lastIndex = 0
  let match
  let key = 0

  MARKDOWN_LINK_PATTERN.lastIndex = 0
  while ((match = MARKDOWN_LINK_PATTERN.exec(body)) !== null) {
    const [full, label, url] = match
    if (match.index > lastIndex) {
      nodes.push(body.slice(lastIndex, match.index))
    }
    if (HTTP_URL_PATTERN.test(url)) {
      nodes.push(
        <Link key={key++} href={url} target="_blank" rel="noopener noreferrer">
          {label}
        </Link>
      )
    } else {
      nodes.push(full)
    }
    lastIndex = match.index + full.length
  }
  if (lastIndex < body.length) {
    nodes.push(body.slice(lastIndex))
  }
  return nodes
}

function getValidActions(actions) {
  if (!Array.isArray(actions)) return []
  return actions
    .filter((action) =>
      typeof action?.label === 'string' &&
      typeof action?.url === 'string' &&
      HTTP_URL_PATTERN.test(action.url)
    )
    .slice(0, MAX_ACTIONS)
}

export default function ConversationAlert({ alert }) {
  if (!alert?.body) return null

  const variant = ALERT_VARIANTS.includes(alert.variant) ? alert.variant : 'info'
  const actions = getValidActions(alert.actions)

  return (
    <Paper
      elevation={0}
      data-testid="conversation-alert"
      data-variant={variant}
      sx={{
        my: 2,
        px: 2,
        py: 1.5,
        borderRadius: 1,
        borderLeft: '4px solid',
        borderLeftColor: `${variant}.main`,
        bgcolor: (theme) => alpha(theme.palette[variant].main, 0.08),
        // rendered inside ReactMarkdown's <pre> wrapper — restore normal text wrapping
        whiteSpace: 'normal',
        overflowWrap: 'break-word',
      }}
    >
      {alert.title && (
        <Typography variant="subtitle2" fontWeight="bold">{alert.title}</Typography>
      )}
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        {renderBodyWithLinks(alert.body)}
      </Typography>
      {actions.length > 0 && (
        <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
          {actions.map((action) => (
            <Button
              key={action.url}
              size="small"
              variant="outlined"
              onClick={() => window.open(action.url, '_blank', 'noopener,noreferrer')}
            >
              {action.label}
            </Button>
          ))}
        </Box>
      )}
    </Paper>
  )
}
